import type { HttpErrorResponse } from '@angular/common/http';
import type { FamilyPlanSettings } from '../types/family-plan-settings.types';
import type { FamilyStartReason } from '../types/family-start.types';
import { familySaleErrorMessage } from '../../components/notification-modal/utils/notification-family-sale.util';
import { formatFamilyValue } from './family-plan-settings.util';

/** Pure rules for Start Family (CWS-1581): what the pop-up says and prices. */

const toCents = (dollars: number): number => Math.round(dollars * 100);
const toDollars = (cents: number): number => cents / 100;

const REASON_MESSAGES: Record<string, string> = {
  FAMILY_MEMBERSHIP_NOT_ACTIVE: 'Only an active membership can start a Family Plan.',
  FAMILY_NOT_BILLED_BY_WAY: 'Only memberships billed through Way can start a Family Plan.',
  FAMILY_PACKAGE_NOT_ELIGIBLE: 'This membership package is not family-enabled.',
  FAMILY_EXISTING_DISCOUNT:
    'This membership already has a discount. A Family Plan cannot be combined with another discount.',
  ALREADY_IN_FAMILY: 'This vehicle or customer is already part of a Family Plan.',
  SUBSCRIPTION_NOT_FOUND: 'We could not find this membership. Refresh and try again.',
};

const FIELD_LABELS: Record<string, string> = { plate: 'license plate', phone: 'phone number' };

/** "Add the license plate and phone number to this membership first." */
export function missingFieldsMessage(fields: readonly string[] | undefined): string {
  const labels = (fields ?? []).map(field => FIELD_LABELS[field] ?? field);
  return labels.length
    ? `Add the ${labels.join(' and ')} to this membership first.`
    : 'This membership is missing details needed to start a Family Plan.';
}

/** What the pop-up shows for a refusal; the server's own message is the last resort. */
export function startFamilyReasonMessage(reason: FamilyStartReason | null | undefined): string {
  if (!reason) {
    return '';
  }
  if (reason.code === 'FAMILY_START_MISSING_FIELDS') {
    return missingFieldsMessage(reason.details);
  }
  return REASON_MESSAGES[reason.code] ?? familySaleErrorMessage(reason.code, reason.message);
}

/** Reads `{ code, message, details }` from a failed request. */
export function startFamilyReasonFromError(error: unknown): FamilyStartReason {
  const http = error as HttpErrorResponse | null;
  const data = http?.error?.data;
  return {
    code: typeof data?.code === 'string' ? data.code : 'UNKNOWN',
    message:
      http?.error?.message ??
      'We could not start the Family Plan. Check your connection and try again.',
    details: Array.isArray(data?.details) ? data.details : undefined,
  };
}

export interface StartFamilyPrice {
  discount: number;
  recurringPrice: number;
}

/** Preview of the renewal price from the next cycle; the server prices the real one. */
export function startFamilyPrice(
  listPrice: number,
  settings: Pick<FamilyPlanSettings, 'discount_type'>,
  value: number
): StartFamilyPrice {
  const listCents = toCents(listPrice);
  const raw =
    settings.discount_type === 'percentage'
      ? Math.round((listCents * value) / 100)
      : toCents(value);
  const discountCents = Math.min(Math.max(raw, 0), listCents);
  return {
    discount: toDollars(discountCents),
    recurringPrice: toDollars(listCents - discountCents),
  };
}

/** The preset choices, labelled "10%" or "$5". */
export function startFamilyOptions(
  settings: FamilyPlanSettings | null
): { value: number; label: string }[] {
  return (settings?.presets ?? []).map(value => ({
    value,
    label: formatFamilyValue(settings?.discount_type ?? null, value),
  }));
}
